import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
    ChevronLeft,
    ChevronRight,
    Plus,
    Video,
    Copy,
    Mail,
    Trash2,
    CalendarDays,
    Clock,
    Users,
    Link2,
    Loader2,
    CheckCircle,
    RefreshCw,
    Eye,
    Repeat,
    Search,
} from 'lucide-react';
import api, { useAuth } from '@/lib/api';
import logger from '@/lib/logger';

// ── Helpers ────────────────────────────────────
const UAE_TZ = 'Asia/Dubai';

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function daysInMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}
function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: UAE_TZ });
}
function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: UAE_TZ });
}
function isSameDay(d1str, d2) {
    const d1 = new Date(d1str);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
function toLocalInput(date) {
    const offset = 4 * 60; // UAE +4
    const d = new Date(date.getTime() + offset * 60000);
    return d.toISOString().slice(0, 16);
}

const EVENT_COLORS = {
    meeting: '#7c3aed',
    class: '#0ea5e9',
    personal: '#22c55e',
};

const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Every day' },
    { value: 'weekly', label: 'Every week (same day)' },
    { value: 'alternate_day', label: 'Every alternate day' },
];

// ── Main Component ─────────────────────────────
export default function CalendarPage() {
    const { user } = useAuth();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: '' });
    const [allUsers, setAllUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewMode, setViewMode] = useState('month'); // month | day
    const [filterUser, setFilterUser] = useState('');
    const [connecting, setConnecting] = useState(false);

    // Check URL params for OAuth callback
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('connected') === 'true') {
            toast.success('Google Calendar connected successfully!');
            window.history.replaceState({}, '', window.location.pathname);
        }
        if (params.get('calendar_error')) {
            toast.error(`Google Calendar error: ${params.get('calendar_error')}`);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const y = currentMonth.getFullYear();
        const m = currentMonth.getMonth();
        const start = new Date(y, m, 1).toISOString();
        const end = new Date(y, m + 1, 0, 23, 59, 59).toISOString();

        try {
            const [evRes, statusRes, usersRes] = await Promise.all([
                api.get(`/calendar/events?start=${start}&end=${end}`),
                api.get('/calendar/status'),
                api.get('/calendar/users'),
            ]);
            setEvents(evRes.data.events || []);
            setCalendarStatus(statusRes.data);
            setAllUsers(usersRes.data.users || []);
        } catch (err) {
            logger.error('Calendar fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [currentMonth]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleConnectGoogle = async () => {
        setConnecting(true);
        try {
            const res = await api.get('/oauth/calendar/connect');
            window.location.href = res.data.authorization_url;
        } catch (err) {
            toast.error('Failed to start Google connection');
            setConnecting(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            await api.delete(`/calendar/events/${eventId}`);
            toast.success('Event deleted');
            setSelectedEvent(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    const handleCopyMeetLink = (link) => {
        navigator.clipboard.writeText(link);
        toast.success('Meet link copied!');
    };

    const handleShareEmail = async (eventId, emails) => {
        try {
            await api.post('/calendar/share', { event_id: eventId, emails, type: 'email' });
            toast.success('Meeting shared via email');
        } catch (err) {
            toast.error('Failed to share');
        }
    };

    // ── Calendar Grid ──────────────────────────
    const calendarDays = useMemo(() => {
        const first = startOfMonth(currentMonth);
        const startDay = first.getDay();
        const total = daysInMonth(currentMonth);
        const days = [];

        for (let i = 0; i < startDay; i++) days.push(null);
        for (let d = 1; d <= total; d++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
            const dayEvents = events.filter(ev => {
                if (filterUser && ev.created_by !== filterUser && !(ev.attendee_ids || []).includes(filterUser)) return false;
                return isSameDay(ev.start_time, date);
            });
            days.push({ date, day: d, events: dayEvents });
        }
        return days;
    }, [currentMonth, events, filterUser]);

    const today = new Date();
    const isToday = (d) => d && d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();

    const selectedDayEvents = useMemo(() => {
        if (!selectedDay) return [];
        return events.filter(ev => {
            if (filterUser && ev.created_by !== filterUser && !(ev.attendee_ids || []).includes(filterUser)) return false;
            return isSameDay(ev.start_time, selectedDay);
        }).sort((a, b) => a.start_time.localeCompare(b.start_time));
    }, [selectedDay, events, filterUser]);

    return (
        <div className="space-y-4" data-testid="calendar-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">My Calendar</h1>
                    <p className="text-sm text-muted-foreground">Schedule meetings, classes & check availability</p>
                </div>
                <div className="flex items-center gap-2">
                    {calendarStatus.connected ? (
                        <Badge className="bg-green-500/15 text-green-500 border border-green-500/30 text-xs" data-testid="google-connected-badge">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Google: {calendarStatus.email}
                        </Badge>
                    ) : (
                        <Button variant="outline" size="sm" onClick={handleConnectGoogle} disabled={connecting} data-testid="connect-google-btn">
                            {connecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Video className="h-3.5 w-3.5 mr-1" />}
                            Connect Google Calendar
                        </Button>
                    )}
                    <Button onClick={() => { setShowCreate(true); }} data-testid="create-event-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Event
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
                {/* Calendar Grid */}
                <Card className="col-span-8 border" data-testid="calendar-grid">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <CardTitle className="text-lg">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </CardTitle>
                                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>Today</Button>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select value={filterUser} onValueChange={setFilterUser}>
                                    <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="filter-user-select">
                                        <SelectValue placeholder="All calendars" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All calendars</SelectItem>
                                        {allUsers.map(u => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.full_name} ({u.role?.replace(/_/g, ' ')})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button variant="ghost" size="icon" onClick={fetchData} title="Refresh">
                                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-2">
                        {/* Day headers */}
                        <div className="grid grid-cols-7 gap-px mb-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d}</div>
                            ))}
                        </div>
                        {/* Calendar cells */}
                        <div className="grid grid-cols-7 gap-px">
                            {calendarDays.map((cell, i) => (
                                <div
                                    key={i}
                                    className={`min-h-[90px] border rounded p-1 cursor-pointer transition-colors
                                        ${!cell ? 'bg-muted/20' : ''}
                                        ${cell && isToday(cell.date) ? 'border-purple-500 bg-purple-500/5' : 'border-muted'}
                                        ${cell && selectedDay && cell.date.toDateString() === selectedDay.toDateString() ? 'ring-2 ring-purple-500/50' : ''}
                                        hover:bg-muted/30`}
                                    onClick={() => cell && setSelectedDay(cell.date)}
                                    data-testid={cell ? `calendar-day-${cell.day}` : undefined}
                                >
                                    {cell && (
                                        <>
                                            <div className={`text-[11px] font-medium mb-0.5 ${isToday(cell.date) ? 'text-purple-500 font-bold' : 'text-muted-foreground'}`}>
                                                {cell.day}
                                            </div>
                                            <div className="space-y-0.5">
                                                {cell.events.slice(0, 3).map(ev => (
                                                    <div
                                                        key={ev.id}
                                                        className="text-[9px] px-1 py-0.5 rounded truncate text-white cursor-pointer"
                                                        style={{ backgroundColor: ev.is_busy_only ? '#64748b' : (EVENT_COLORS[ev.event_type] || ev.color || '#7c3aed') }}
                                                        onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                                                        title={ev.title}
                                                    >
                                                        {ev.is_busy_only ? 'Busy' : ev.title}
                                                    </div>
                                                ))}
                                                {cell.events.length > 3 && (
                                                    <div className="text-[9px] text-muted-foreground text-center">+{cell.events.length - 3} more</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Legend */}
                        <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
                            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded" style={{ background: EVENT_COLORS.meeting }} /> Meeting</div>
                            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded" style={{ background: EVENT_COLORS.class }} /> Class</div>
                            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded" style={{ background: EVENT_COLORS.personal }} /> Personal</div>
                            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-slate-500" /> Busy (private)</div>
                        </div>
                    </CardContent>
                </Card>

                {/* Right sidebar: Day detail + Quick availability */}
                <div className="col-span-4 space-y-4">
                    {/* Day Detail */}
                    <Card className="border" data-testid="day-detail">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <CalendarDays className="h-4 w-4 text-purple-500" />
                                {selectedDay ? formatDate(selectedDay.toISOString()) : 'Select a day'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!selectedDay ? (
                                <p className="text-xs text-muted-foreground">Click on a day to see events</p>
                            ) : selectedDayEvents.length === 0 ? (
                                <div className="text-center py-6">
                                    <p className="text-xs text-muted-foreground mb-2">No events</p>
                                    <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
                                        <Plus className="h-3 w-3 mr-1" /> Create
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                    {selectedDayEvents.map(ev => (
                                        <div
                                            key={ev.id}
                                            className="p-2.5 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                                            style={{ borderLeftColor: ev.is_busy_only ? '#64748b' : (EVENT_COLORS[ev.event_type] || '#7c3aed'), borderLeftWidth: '3px' }}
                                            onClick={() => !ev.is_busy_only && setSelectedEvent(ev)}
                                            data-testid={`day-event-${ev.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium truncate">{ev.is_busy_only ? `${ev.created_by_name} — Busy` : ev.title}</span>
                                                {ev.google_meet_link && <Video className="h-3 w-3 text-purple-500 shrink-0" />}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                                {formatTime(ev.start_time)} — {formatTime(ev.end_time)}
                                                {!ev.is_busy_only && ev.created_by_name && <span className="ml-2">by {ev.created_by_name}</span>}
                                            </div>
                                            {ev.recurrence_type && ev.recurrence_type !== 'none' && (
                                                <Badge variant="outline" className="text-[9px] mt-1 px-1 py-0">
                                                    <Repeat className="h-2.5 w-2.5 mr-0.5" />
                                                    {RECURRENCE_OPTIONS.find(r => r.value === ev.recurrence_type)?.label || ev.recurrence_type}
                                                </Badge>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Availability Check */}
                    <AvailabilityChecker allUsers={allUsers} selectedDay={selectedDay} />
                </div>
            </div>

            {/* Create Event Dialog */}
            <CreateEventDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                allUsers={allUsers}
                onCreated={() => { setShowCreate(false); fetchData(); }}
                defaultDate={selectedDay}
                calendarConnected={calendarStatus.connected}
            />

            {/* Event Detail Dialog */}
            {selectedEvent && !selectedEvent.is_busy_only && (
                <EventDetailDialog
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    onDelete={handleDeleteEvent}
                    onCopyLink={handleCopyMeetLink}
                    onShareEmail={handleShareEmail}
                    currentUserId={user?.id}
                    currentUserRole={user?.role}
                />
            )}
        </div>
    );
}

// ── Create Event Dialog ────────────────────────
function CreateEventDialog({ open, onClose, allUsers, onCreated, defaultDate, calendarConnected }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [eventType, setEventType] = useState('meeting');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [recurrenceType, setRecurrenceType] = useState('none');
    const [recurrenceUntil, setRecurrenceUntil] = useState('');
    const [attendeeIds, setAttendeeIds] = useState([]);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && defaultDate) {
            const d = new Date(defaultDate);
            d.setHours(9, 0, 0, 0);
            setStartTime(toLocalInput(d));
            d.setHours(10, 0, 0, 0);
            setEndTime(toLocalInput(d));
        }
    }, [open, defaultDate]);

    const handleCreate = async () => {
        if (!title.trim()) { toast.error('Title required'); return; }
        if (!startTime || !endTime) { toast.error('Times required'); return; }

        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                description,
                start_time: new Date(startTime).toISOString(),
                end_time: new Date(endTime).toISOString(),
                event_type: eventType,
                recurrence_type: recurrenceType,
                recurrence_until: recurrenceUntil ? new Date(recurrenceUntil).toISOString() : null,
                attendee_ids: attendeeIds,
                color: EVENT_COLORS[eventType] || '#7c3aed',
            };
            const res = await api.post('/calendar/events', payload);
            const meetLink = res.data.event?.google_meet_link;
            toast.success(meetLink ? 'Event created with Google Meet link!' : 'Event created');
            resetForm();
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create event');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setTitle(''); setDescription(''); setEventType('meeting');
        setStartTime(''); setEndTime(''); setRecurrenceType('none');
        setRecurrenceUntil(''); setAttendeeIds([]); setSearch('');
    };

    const filteredUsers = allUsers.filter(u =>
        u.full_name.toLowerCase().includes(search.toLowerCase()) && !attendeeIds.includes(u.id)
    );
    const selectedAttendees = allUsers.filter(u => attendeeIds.includes(u.id));

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) { resetForm(); onClose(); } }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-event-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5 text-purple-500" /> New Calendar Event
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label>Title *</Label>
                        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Team Standup, Forex 101 Class" data-testid="event-title-input" />
                    </div>

                    <div>
                        <Label>Description</Label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Meeting agenda or notes..." rows={2} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Type</Label>
                            <Select value={eventType} onValueChange={setEventType}>
                                <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="meeting">Meeting</SelectItem>
                                    <SelectItem value="class">Class</SelectItem>
                                    <SelectItem value="personal">Personal</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Repeats</Label>
                            <Select value={recurrenceType} onValueChange={setRecurrenceType}>
                                <SelectTrigger data-testid="recurrence-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {RECURRENCE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Start</Label>
                            <Input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="event-start-input" />
                        </div>
                        <div>
                            <Label>End</Label>
                            <Input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} data-testid="event-end-input" />
                        </div>
                    </div>

                    {recurrenceType !== 'none' && (
                        <div>
                            <Label>Repeat until (optional)</Label>
                            <Input type="date" value={recurrenceUntil} onChange={e => setRecurrenceUntil(e.target.value)} />
                        </div>
                    )}

                    {/* Attendees */}
                    <div>
                        <Label className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Attendees</Label>
                        {selectedAttendees.length > 0 && (
                            <div className="flex flex-wrap gap-1 my-1.5">
                                {selectedAttendees.map(u => (
                                    <Badge key={u.id} variant="outline" className="text-xs pr-1">
                                        {u.full_name}
                                        <button className="ml-1 hover:text-red-500" onClick={() => setAttendeeIds(prev => prev.filter(id => id !== u.id))}>x</button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <div className="relative">
                            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search people to invite..."
                                className="pl-8 h-9 text-xs"
                                data-testid="attendee-search"
                            />
                        </div>
                        {search && (
                            <div className="border rounded mt-1 max-h-32 overflow-y-auto">
                                {filteredUsers.slice(0, 10).map(u => (
                                    <button
                                        key={u.id}
                                        className="w-full flex items-center justify-between text-xs p-2 hover:bg-muted transition-colors text-left"
                                        onClick={() => { setAttendeeIds(prev => [...prev, u.id]); setSearch(''); }}
                                    >
                                        <span>{u.full_name} <span className="text-muted-foreground">({u.role?.replace(/_/g, ' ')})</span></span>
                                        <Plus className="h-3 w-3 text-purple-500" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {!calendarConnected && (
                        <p className="text-[10px] text-amber-500">Google Calendar not connected — event will be saved locally without a Meet link.</p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => { resetForm(); onClose(); }}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={saving} data-testid="save-event-btn">
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Create Event
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── Event Detail Dialog ────────────────────────
function EventDetailDialog({ event, onClose, onDelete, onCopyLink, onShareEmail, currentUserId, currentUserRole }) {
    const canEdit = event.created_by === currentUserId || ['super_admin', 'admin', 'coo'].includes(currentUserRole);

    return (
        <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="event-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ background: EVENT_COLORS[event.event_type] || event.color || '#7c3aed' }} />
                        {event.title}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(event.start_time)}</span>
                        <span className="text-muted-foreground">{formatTime(event.start_time)} — {formatTime(event.end_time)}</span>
                    </div>

                    {event.description && (
                        <p className="text-sm text-muted-foreground">{event.description}</p>
                    )}

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        Created by {event.created_by_name}
                    </div>

                    {event.recurrence_type && event.recurrence_type !== 'none' && (
                        <Badge variant="outline" className="text-xs">
                            <Repeat className="h-3 w-3 mr-1" />
                            {RECURRENCE_OPTIONS.find(r => r.value === event.recurrence_type)?.label}
                        </Badge>
                    )}

                    {event.attendees && event.attendees.length > 0 && (
                        <div>
                            <p className="text-xs font-medium mb-1">Attendees:</p>
                            <div className="flex flex-wrap gap-1">
                                {event.attendees.map((a, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px]">{a.name}</Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Google Meet Link */}
                    {event.google_meet_link && (
                        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Video className="h-4 w-4 text-purple-500" />
                                    <a href={event.google_meet_link} target="_blank" rel="noopener noreferrer"
                                       className="text-sm text-purple-400 hover:underline font-medium">
                                        Join Google Meet
                                    </a>
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onCopyLink(event.google_meet_link)} title="Copy link">
                                        <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7"
                                        onClick={() => {
                                            const emails = (event.attendees || []).map(a => a.email).filter(Boolean);
                                            if (emails.length) onShareEmail(event.id, emails);
                                            else toast.info('No attendee emails to share with');
                                        }}
                                        title="Share via email">
                                        <Mail className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1 break-all">{event.google_meet_link}</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    {canEdit && (
                        <Button variant="destructive" size="sm" onClick={() => onDelete(event.id)} data-testid="delete-event-btn">
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── Availability Checker ───────────────────────
function AvailabilityChecker({ allUsers, selectedDay }) {
    const [checkUsers, setCheckUsers] = useState([]);
    const [availability, setAvailability] = useState({});
    const [checking, setChecking] = useState(false);
    const [search, setSearch] = useState('');

    const handleCheck = async () => {
        if (!checkUsers.length || !selectedDay) return;
        setChecking(true);
        try {
            const date = selectedDay.toISOString().slice(0, 10);
            const res = await api.get(`/calendar/availability?user_ids=${checkUsers.join(',')}&date=${date}`);
            setAvailability(res.data.availability || {});
        } catch (err) {
            toast.error('Failed to check availability');
        } finally {
            setChecking(false);
        }
    };

    useEffect(() => {
        if (checkUsers.length > 0 && selectedDay) handleCheck();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay]);

    const filteredSearch = allUsers.filter(u =>
        u.full_name.toLowerCase().includes(search.toLowerCase()) && !checkUsers.includes(u.id)
    );

    const selectedNames = allUsers.filter(u => checkUsers.includes(u.id));

    return (
        <Card className="border" data-testid="availability-checker">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="h-4 w-4 text-purple-500" /> Check Availability
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {/* Selected users */}
                {selectedNames.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {selectedNames.map(u => (
                            <Badge key={u.id} variant="outline" className="text-[10px] pr-1">
                                {u.full_name}
                                <button className="ml-1 hover:text-red-500" onClick={() => setCheckUsers(prev => prev.filter(id => id !== u.id))}>x</button>
                            </Badge>
                        ))}
                    </div>
                )}

                <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-2 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Add people to check..."
                        className="pl-8 h-8 text-xs"
                    />
                </div>
                {search && (
                    <div className="border rounded max-h-28 overflow-y-auto">
                        {filteredSearch.slice(0, 8).map(u => (
                            <button key={u.id} className="w-full flex items-center justify-between text-[10px] p-1.5 hover:bg-muted"
                                onClick={() => { setCheckUsers(prev => [...prev, u.id]); setSearch(''); }}>
                                <span>{u.full_name}</span>
                                <Plus className="h-2.5 w-2.5 text-purple-500" />
                            </button>
                        ))}
                    </div>
                )}

                {checkUsers.length > 0 && (
                    <Button size="sm" variant="outline" onClick={handleCheck} disabled={checking || !selectedDay} className="w-full text-xs">
                        {checking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
                        Check {selectedDay ? formatDate(selectedDay.toISOString()) : '(select a day)'}
                    </Button>
                )}

                {/* Results */}
                {Object.keys(availability).length > 0 && (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {checkUsers.map(uid => {
                            const u = allUsers.find(x => x.id === uid);
                            const data = availability[uid];
                            const isFree = !data || data.total_meetings === 0;
                            return (
                                <div key={uid} className="flex items-center justify-between p-2 rounded border text-xs">
                                    <span className="font-medium">{u?.full_name || uid}</span>
                                    {isFree ? (
                                        <Badge className="bg-green-500/15 text-green-500 border-green-500/30 text-[9px]">Free</Badge>
                                    ) : (
                                        <div className="text-right">
                                            <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30 text-[9px]">{data.total_meetings} meeting(s)</Badge>
                                            <div className="text-[9px] text-muted-foreground mt-0.5">
                                                {data.busy_slots?.map((s, i) => (
                                                    <span key={i}>{s.is_busy_only ? 'Busy' : s.title}: {formatTime(s.start_time)}–{formatTime(s.end_time)}{i < data.busy_slots.length - 1 ? ', ' : ''}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
